import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useContext, useState, useEffect, useRef } from 'react'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { CustomerCompany } from '../types'
import { numberWithCommas } from '../utils'
import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import { BankingPartner } from '../types/user.types'
import NextIcon from '../assets/icons/next-icon.svg'
import { requestAccessToken } from '../api/auth.api'
import { AuthContext, UserContext } from '../contexts'
import ChartIcon from '../assets/icons/chart-icon.svg'
import '../styles/customer-company-details-management.css'
import AccountIcon from '../assets/icons/account-icon.svg'
import FundingsIcon from '../assets/icons/fundings-icon.svg'
import MoneyGreenIcon from '../assets/icons/money-icon-green.svg'
import { getCurrentBankingPartners } from '../api/app-queries.api'
import { getCustomerCompanyDetailsData } from '../api/app-data.api'
import PaymentsPaidIcon from '../assets/icons/payments-paid-icon.svg'
import ContractDeleteIcon from '../assets/icons/contract-delete-icon.svg'
import '../components/customer-company-details-management/styles/index.css'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import { ReportSquareContainer } from '../components/reports-brief-management/report-square-container'
import { ReconciliationBriefModule } from '../components/customer-company-details-management/reconciliation-brief-module'
import { CustomerCompanyProfileData } from '../components/customer-company-details-management/customer-company-profile-data'
import { CustomerCompanyBalanceData } from '../components/customer-company-details-management/customer-company-balance-data'
import { CustomerCompanyFeesData } from '../components/customer-companies-management/new-customer-company-form-banking-partners'
import { AddNewCustomerCompanyFundingModal } from '../components/customer-companies-management/add-new-customer-company-funding-modal'
import { AddNewCustomerCompanyModal, CompanyOnboardingData } from '../components/customer-companies-management/add-new-customer-company-modal'

const CustomerCompanyDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const { userSession } = useContext(UserContext)
  const componentDidMount = useRef<boolean>(false)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { t } = useTranslation(['customer-company-details-management', 'customer-companies-management', 'reports-management'])

  const [tradedVolume, setTradedVolume] = useState<number>(0)
  const [paymentsQuantity, setPaymentsQuantity] = useState<number>(0)
  const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false)
  const [paymentsPaidAmount, setPaymentsPaidAmount] = useState<number>(0)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentsPaidQuantity, setPaymentsPaidQuantity] = useState<number>(0)
  const [availableCurrencies, setAvailableCurrencies] = useState<Array<string>>([])
  const [paymentsPendingQuantity, setPaymentsPendingQuantity] = useState<number>(0)
  const [noveltiesSolvedQuantity, setNoveltiesSolvedQuantity] = useState<number>(0)
  const [queriesOfRestrictiveList, setQueriesOfRestrictiveList] = useState<number>(0)
  const [paymentsReturnedQuantity, setPaymentsReturnedQuantity] = useState<number>(0)
  const [paymentsValidatedQuantity, setPaymentsValidatedQuantity] = useState<number>(0)
  const [customerCompanyData, setCustomerCompanyData] = useState<CustomerCompany | null>(null)
  const [paymentsWithNoveltiesQuantity, setPaymentsWithNoveltiesQuantity] = useState<number>(0)
  const [customerCompanyFundingsAmount, setCustomerCompanyFundingsAmount] = useState<number>(0)
  const [currentBankingPartners, setCurrentBankingPartners] = useState<Array<BankingPartner>>([])
  const [paymentsPendingRoutingQuantity, setPaymentsPendingRoutingQuantity] = useState<number>(0)
  const [paymentsErrorOrRejectedQuantity, setPaymentsErrorOrRejectedQuantity] = useState<number>(0)
  const [customerCompanyFundingsQuantity, setCustomerCompanyFundingsQuantity] = useState<number>(0)
  const [openUpdateCustomerCompanyDataModal, setOpenUpdateCustomerCompanyDataModal] = useState<boolean>(false)
  const [openNewCustomerCompanyFundingModal, setOpenNewCustomerCompanyFundingModal] = useState<boolean>(false)
  const [customerCompanyDataToUpdate, setCustomerCompanyDataToUpdate] = useState<CompanyOnboardingData | null>(null)
  const [bankingPartnersDataToUpdate, setBankingPartnersDataToUpdate] = useState<Array<CustomerCompanyFeesData>>([])
  const [customerCompanyProfile, setCustomerCompanyProfile] = useState<{ balance: string; businessName: string; businessDocumentNumber: string; businessEmail: string; businessOwner: string }>()

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'admin' && userSession?.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetCurrentBankingPartners()
    handleGetCustomerCompanyDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('addNewCustomerCompanyFunding', async (dataFromSocket: any) => {
      if (String(dataFromSocket.customerCompanyId) === String(customerCompanyId)) {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetCustomerCompanyDetailsFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('deleteCustomerCompanyFunding', async (dataFromSocket: any) => {
      if (String(dataFromSocket.customerCompanyId) === String(customerCompanyId)) {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetCustomerCompanyDetailsFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('updateCustomerCompany', async (dataFromSocket: any) => {
      if (String(dataFromSocket.customerCompanyDataUpdated._id) === String(customerCompanyId)) {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetCustomerCompanyDetailsFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('deleteCustomerCompany', async (dataFromSocket: any) => {
      if (String(dataFromSocket.documentKey) === String(customerCompanyId)) {
        handleDeleteCustomerCompanyFromLocalDB(String(dataFromSocket.documentKey))
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized, customerCompanyId])

  const handleGetCurrentBankingPartners = async () => {
    let localCurrentBankingPartnersToSet: any = {}
    const localCurrentBankingPartners = globalThis.sessionStorage.getItem('currentBankingPartners')

    if (localCurrentBankingPartners) {
      localCurrentBankingPartnersToSet = JSON.parse(localCurrentBankingPartners)
      handleSetBankingPartnersInitialData(localCurrentBankingPartnersToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getCurrentBankingPartners(accessToken, customerCompanyId)

      globalThis.sessionStorage.setItem('currentBankingPartners', JSON.stringify(data))
      handleSetBankingPartnersInitialData(data)
    } catch (error) {
      console.log('Error handleGetHomeData > ', error)
    }
  }

  const handleSetBankingPartnersInitialData = async (dataToSet: any) => {
    setAvailableCurrencies(dataToSet.availableCurrencies)
    setCurrentBankingPartners(dataToSet.currentBankingPartners)
    setBankingPartnersDataToUpdate(dataToSet.bankingPartnersData)
    setCustomerCompanyDataToUpdate(dataToSet.customerCompanyData)
  }

  const handleGetCustomerCompanyDetails = async () => {
    let localCustomerCompanyDetailsToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localCustomerCompanyDetails = globalThis.sessionStorage.getItem(`customerCompanyDetails:${customerCompanyId}:${selectedCountry}`)

    if (localCustomerCompanyDetails) {
      localCustomerCompanyDetailsToSet = JSON.parse(localCustomerCompanyDetails)
      handleSetCustomerCompanyDetailsInitialData(localCustomerCompanyDetailsToSet)
    }

    handleGetCustomerCompanyDetailsFromRemote(selectedCountry)
  }

  const handleGetCustomerCompanyDetailsFromRemote = async (selectedCountry: string) => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getCustomerCompanyDetailsData(String(customerCompanyId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`customerCompanyDetails:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
      handleSetCustomerCompanyDetailsInitialData(data /*, accessToken */)
    } catch (error) {
      console.log('Error handleGetCustomerCompanyDetails > ', error)
    }
  }

  const handleSetCustomerCompanyDetailsInitialData = async (dataToSet: any /*, accessToken?: string*/) => {
    setCustomerCompanyProfile({
      balance: dataToSet.customerCompanyData.balance,
      businessName: dataToSet.customerCompanyData.businessCompany.name.toUpperCase(),
      businessDocumentNumber: dataToSet.customerCompanyData.businessCompany.documentNumber,
      businessEmail: dataToSet.customerCompanyData.businessCompany.email || dataToSet.customerCompanyData.companyMainUserData.email,
      businessOwner: dataToSet.customerCompanyData.businessOwner?.firstName
        ? `${dataToSet.customerCompanyData.businessOwner.firstName.toUpperCase()} ${dataToSet.customerCompanyData.businessOwner.lastName.toUpperCase()}`
        : `${dataToSet.customerCompanyData.companyMainUserData.firstName.toUpperCase()} ${dataToSet.customerCompanyData.companyMainUserData.lastName.toUpperCase()}`
    })

    setCustomerCompanyData({ ...dataToSet.customerCompanyData })

    setTradedVolume(dataToSet.paymentStats[0] ? dataToSet.paymentStats[0].amountSum : 0)
    setPaymentsQuantity(dataToSet.paymentStats[0].totalDocs)
    setPaymentsPaidAmount(dataToSet.paymentStats[0].amountSum)
    setPaymentsPendingQuantity(
      Number(dataToSet.paymentStats[0].totalDocs) -
        Number(dataToSet.paymentStats[0].statusCounts.paid) -
        Number(dataToSet.paymentStats[0].statusCounts.rejected) -
        Number(dataToSet.paymentStats[0].statusCounts.returned) -
        Number(dataToSet.paymentStats[0].statusCounts.validated)
    )
    setQueriesOfRestrictiveList(dataToSet.paymentStats[0].totalDocs)
    setPaymentsPendingRoutingQuantity(dataToSet.paymentsPendingRouting)
    setNoveltiesSolvedQuantity(dataToSet.paymentsWithNoveltiesApproved)
    setPaymentsPaidQuantity(dataToSet.paymentStats[0].statusCounts.paid)
    setPaymentsReturnedQuantity(dataToSet.paymentStats[0].statusCounts.returned)
    setPaymentsValidatedQuantity(dataToSet.paymentStats[0].statusCounts.validated)
    setPaymentsErrorOrRejectedQuantity(Number(dataToSet.paymentStats[0].statusCounts.rejected))
    setCustomerCompanyFundingsQuantity(dataToSet.customerCompanyFundingsData[0] ? dataToSet.customerCompanyFundingsData[0].count : 0)
    setPaymentsWithNoveltiesQuantity(Number(dataToSet.paymentStats[0].noveltiesNotEmpty) + Number(dataToSet.paymentStats[0].tagsNotEmpty))
    setCustomerCompanyFundingsAmount(dataToSet.customerCompanyFundingsData[0] ? dataToSet.customerCompanyFundingsData[0].totalPurchaseNetValue : 0)

    setLoadingLocalData(false)
    dataFullyLoadedRef.current = true

    if (componentDidMount.current === false) {
      setDataFullyLoaded(true)
      componentDidMount.current = true
    }
  }

  const handleDeleteCustomerCompanyFromLocalDB = async (documentKey: string) => {
    const customerCompanyDataSetted: Array<CustomerCompany> = (await handleGetDataFromTableById('customer-companies', String(documentKey))) as Array<CustomerCompany>

    if (customerCompanyDataSetted.length > 0) {
      await handleDeleteDataByOne('customer-companies', documentKey)
    }

    navigate('/')
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.secondLevel')}
        </Typography>
      </Breadcrumbs>
      <div className="customer-details-profile-header">
        {loadingLocalData ? (
          <div className="customer-company-details-profile-data-loading"></div>
        ) : (
          <CustomerCompanyProfileData
            handleCloseSession={handleCloseSession}
            customerCompanyData={customerCompanyData}
            customerCompanyProfile={customerCompanyProfile}
            handleOpenUpdateCustomerCompanyData={() => setOpenUpdateCustomerCompanyDataModal(true)}
          />
        )}
        {loadingLocalData ? (
          <div className="customer-details-profile-payments-brief-data-loading"></div>
        ) : (
          <CustomerCompanyBalanceData
            selectedCountry={selectedCountry}
            customerCompanyProfile={customerCompanyProfile}
            handleOpenCustomerCompanyFundingDataModal={() => setOpenNewCustomerCompanyFundingModal(true)}
          />
        )}
      </div>
      {userSession?.subRole && userSession?.subRole === 'reconciliation-specialist' ? (
        <>
          <div className="reports-row-container customer-company-reports-row-container">
            <ReportSquareContainer
              showMainIcon
              icon={FundingsIcon}
              showSecondaryData={false}
              reportName={t('reportsNames.totalFundings', { ns: 'reports-management' })}
              mainData={`$ ${numberWithCommas(Number(customerCompanyFundingsAmount).toFixed(2))} ${(countriesCurrencies as { [key: string]: string })[String(selectedCountry)].toUpperCase()}`}
            />
            <ReportSquareContainer
              showMainIcon
              icon={PaymentsPaidIcon}
              showSecondaryData={false}
              reportName={t('reportsNames.totalPaid', { ns: 'reports-management' })}
              mainData={`$ ${numberWithCommas(Number(paymentsPaidAmount).toFixed(2))} ${(countriesCurrencies as { [key: string]: string })[String(selectedCountry)].toUpperCase()}`}
            />
          </div>
          <ReconciliationBriefModule customerCompanyProfile={customerCompanyProfile} />
        </>
      ) : (
        <>
          <div className="reports-row-container customer-company-reports-row-container">
            {userSession && String(userSession.subRole) === 'payments-specialist' && (
              <ReportSquareContainer
                showMainIcon
                icon={ChartIcon}
                showButtonGoDetails
                showSecondaryData={false}
                dataFullyLoaded={dataFullyLoaded}
                reportName={t('reportsNames.tradedVolume', { ns: 'reports-management' })}
                detailsButtonText={t('detailsButtonTexts.viewPayments', { ns: 'reports-management' })}
                handleButtonGoDetailsAction={() => navigate(`/customer-company-payments-history/${customerCompanyId}`)}
                mainData={`$ ${numberWithCommas(Number(tradedVolume || 0).toFixed(2))} ${(countriesCurrencies as { [key: string]: string })[String(selectedCountry)].toUpperCase()}`}
              />
            )}
            <ReportSquareContainer
              loaderWidth={45}
              showButtonGoDetails
              showMainIcon={false}
              showSecondaryData={false}
              dataFullyLoaded={dataFullyLoaded}
              mainData={numberWithCommas(customerCompanyFundingsQuantity)}
              reportName={t('reportsNames.fundingsMade', { ns: 'reports-management' })}
              detailsButtonText={t('detailsButtonTexts.viewFundings', { ns: 'reports-management' })}
              handleButtonGoDetailsAction={() => navigate(`/customer-company-fundings-history/${customerCompanyId}`)}
            />
            <ReportSquareContainer
              loaderWidth={45}
              showButtonGoDetails
              showMainIcon={false}
              showSecondaryData={false}
              dataFullyLoaded={dataFullyLoaded}
              mainData={numberWithCommas(paymentsQuantity)}
              reportName={t('reportsNames.paymentsMade', { ns: 'reports-management' })}
              detailsButtonText={t('detailsButtonTexts.viewMovementsHistory', { ns: 'reports-management' })}
              handleButtonGoDetailsAction={() => navigate(`/customer-company-money-movements/${customerCompanyId}`)}
            />
          </div>
          {((userSession && !userSession.subRole) || (userSession?.subRole && String(userSession.subRole) !== 'payments-specialist')) && (
            <div className="reports-row-container-slider">
              <div className="reports-row-container customer-company-reports-row-container">
                <ReportSquareContainer
                  showMainIcon
                  icon={ChartIcon}
                  showSecondaryData={false}
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={`$ ${numberWithCommas(Number(tradedVolume || 0).toFixed(2))} ${(countriesCurrencies as { [key: string]: string })[String(selectedCountry)].toUpperCase()}`}
                  reportName={t('reportsNames.tradedVolume', { ns: 'reports-management' })}
                />
                <ReportSquareContainer
                  showMainIcon
                  loaderWidth={60}
                  icon={ContractDeleteIcon}
                  showSecondaryData={false}
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={numberWithCommas(queriesOfRestrictiveList)}
                  reportName={t('reportsNames.queriesOnRestrictiveLists', { ns: 'reports-management' })}
                />
                <ReportSquareContainer
                  showMainIcon
                  loaderWidth={60}
                  icon={AccountIcon}
                  showSecondaryData={false}
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={numberWithCommas(noveltiesSolvedQuantity)}
                  reportName={t('reportsNames.beneficiaryNovelties', { ns: 'reports-management' })}
                />
              </div>
            </div>
          )}
          <div className="reports-row-container-slider">
            <div className="reports-row-container customer-company-reports-row-container">
              {userSession && String(userSession.subRole) === 'payments-specialist' && (
                <ReportSquareContainer
                  showMainIcon
                  showWarningIcon
                  loaderWidth={60}
                  icon={MoneyGreenIcon}
                  showSecondaryData={false}
                  backgroundColor="#CCFBEF"
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={`${numberWithCommas(paymentsPendingRoutingQuantity)}`}
                  reportName={t('reportsNames.pendingPaymentRouting', { ns: 'reports-management' })}
                  handleOnClickFunction={() => navigate(`/payments-pending-routing/${customerCompanyId}`)}
                />
              )}
              {userSession && String(userSession.subRole) === 'compliance-specialist' && (
                <ReportSquareContainer
                  showMainIcon
                  showWarningIcon
                  loaderWidth={60}
                  icon={MoneyGreenIcon}
                  showSecondaryData={false}
                  backgroundColor="#CCFBEF"
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={`${numberWithCommas(paymentsWithNoveltiesQuantity)}`}
                  reportName={t('reportsNames.paymentsWithNovelties', { ns: 'reports-management' })}
                  handleOnClickFunction={() => navigate(`/payments-with-alerts/${customerCompanyId}`)}
                />
              )}
              <ReportSquareContainer
                showMainIcon
                showCheckIcon
                loaderWidth={60}
                icon={MoneyGreenIcon}
                showSecondaryData={false}
                backgroundColor="#CCFBEF"
                dataFullyLoaded={dataFullyLoaded}
                mainData={`${numberWithCommas(paymentsPaidQuantity)}`}
                reportName={t('reportsNames.successfulPayments', { ns: 'reports-management' })}
              />
              <ReportSquareContainer
                showMainIcon
                showValidatedIcon
                loaderWidth={60}
                icon={MoneyGreenIcon}
                showSecondaryData={false}
                backgroundColor="#CCFBEF"
                dataFullyLoaded={dataFullyLoaded}
                mainData={`${numberWithCommas(paymentsValidatedQuantity)}`}
                reportName={t('reportsNames.validatedPayments', { ns: 'reports-management' })}
              />
              <ReportSquareContainer
                showMainIcon
                showReturnedIcon
                loaderWidth={60}
                icon={MoneyGreenIcon}
                showSecondaryData={false}
                backgroundColor="#CCFBEF"
                dataFullyLoaded={dataFullyLoaded}
                mainData={`${numberWithCommas(paymentsReturnedQuantity)}`}
                reportName={t('reportsNames.returnedPayments', { ns: 'reports-management' })}
              />
              <ReportSquareContainer
                showMainIcon
                showCancelIcon
                loaderWidth={60}
                icon={MoneyGreenIcon}
                showSecondaryData={false}
                backgroundColor="#CCFBEF"
                dataFullyLoaded={dataFullyLoaded}
                mainData={`${numberWithCommas(paymentsErrorOrRejectedQuantity)}`}
                reportName={t('reportsNames.rejectedPayments', { ns: 'reports-management' })}
              />
              {(!userSession?.subRole || (userSession?.subRole && String(userSession?.subRole) !== 'compliance-specialist' && String(userSession?.subRole) !== 'payments-specialist')) && (
                <ReportSquareContainer
                  showMainIcon
                  showPendingIcon
                  loaderWidth={60}
                  icon={MoneyGreenIcon}
                  showSecondaryData={false}
                  backgroundColor="#CCFBEF"
                  dataFullyLoaded={dataFullyLoaded}
                  mainData={`${numberWithCommas(paymentsPendingQuantity)}`}
                  reportName={t('reportsNames.pendingPayments', { ns: 'reports-management' })}
                />
              )}
            </div>
          </div>
          {openUpdateCustomerCompanyDataModal && (
            <AddNewCustomerCompanyModal
              customerCompanyId={customerCompanyId}
              handleCloseSession={handleCloseSession}
              availableCurrencies={availableCurrencies}
              currentBankingPartners={currentBankingPartners}
              bankingPartnersDataToUpdate={bankingPartnersDataToUpdate}
              customerCompanyDataToUpdate={customerCompanyDataToUpdate}
              handleGetCurrentBankingPartners={handleGetCurrentBankingPartners}
              handleOpenStateOfModal={(openState: boolean) => setOpenUpdateCustomerCompanyDataModal(openState)}
            />
          )}
          {openNewCustomerCompanyFundingModal && (
            <AddNewCustomerCompanyFundingModal
              handleCloseSession={handleCloseSession}
              customerCompanyData={customerCompanyData}
              handleOpenStateOfModal={(openState: boolean) => {
                setOpenNewCustomerCompanyFundingModal(openState)
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CustomerCompanyDetailsManagement
